import { Button, Card, CardContent } from "@repo/ui";
import { RefreshCw, AlertCircle } from "lucide-react";
import { useAnalyticsData, type DateRange } from "@/hooks/use-analytics-data";
import { TransactionTrendsChart } from "./transaction-trends-chart";
import { InventoryStatusOverview } from "./inventory-status-overview";
import { TopItemsChart } from "./top-items-chart";
import { AnalyticsSkeleton } from "./analytics-skeleton";

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

export function AnalyticsDashboard() {
  const { data, isLoading, error, dateRange, setDateRange, refetch } =
    useAnalyticsData();

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-3 rounded-full bg-destructive/10 p-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">
            Failed to load analytics
          </p>
          <p className="text-xs text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <div
          className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1"
          data-testid="analytics-date-range-selector"
        >
          {DATE_RANGE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={dateRange === option.value ? "default" : "ghost"}
              size="sm"
              className={
                dateRange === option.value
                  ? "h-8 px-3 text-xs"
                  : "h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
              }
              onClick={() => setDateRange(option.value)}
              data-testid={`analytics-date-range-${option.value}`}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refetch}
          disabled={isLoading}
          className="text-muted-foreground"
          data-testid="analytics-refresh-button"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <AnalyticsSkeleton />
      ) : data ? (
        <>
          {/* Inventory Status Overview */}
          <div data-testid="analytics-inventory-status">
            <InventoryStatusOverview
              status={data.inventoryStatus}
              totalItems={data.totalItems}
            />
          </div>

          {/* Transaction Trends */}
          <div data-testid="analytics-transaction-trends">
            <TransactionTrendsChart data={data.trends} dateRange={dateRange} />
          </div>

          {/* Top Items */}
          <div data-testid="analytics-top-items">
            <TopItemsChart
              topDispensed={data.topDispensed}
              topReceived={data.topReceived}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
